/** Libraries */
import Swal from "sweetalert2";

/** Utils */
import Toast from "@moltech/molui/toast";
import { recordDevApiError } from "../dev/devApiErrorStore";

/** Encryption */
import { decryptAES } from "../encryption/CryptoService";

const ensureAuthStatusCode = (apiData, meta) => {
    if (apiData?.statusCode || !isAuthenticationUrl(meta)) return;

    const authFlag = apiData?.isAuthenticated ?? apiData?.responseItem?.responseContent?.isAuthenticated;
    if (authFlag !== undefined) {
        apiData.statusCode = authFlag ? 200 : 401;
    }
};

const isAuthenticationUrl = (meta) => meta.request.url.includes("Authentication");

const isSuccessResponse = (apiData) => {
    if (Array.isArray(apiData)) return true;
    if (apiData == null || typeof apiData !== "object" || apiData instanceof Blob || (!!apiData && typeof apiData === 'object'))
         return true;

    const success = apiData.success ?? apiData.Success;
    if (success === false) return false;

    const statusCode = apiData.statusCode ?? apiData.StatusCode;
    if (typeof statusCode === "number" && statusCode >= 400) return false;

    return true;
};

const isBusinessFailurePayload = (payload) => {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        return false;
    }
    const success = payload.success ?? payload.Success;
    if (success === false) return true;
    const statusCode = payload.statusCode ?? payload.StatusCode;
    return typeof statusCode === "number" && statusCode >= 400;
};

const captureBusinessFailureIfNeeded = (meta, payload, raw) => {
    if (!isBusinessFailurePayload(payload)) return;
    const msg =
        payload.message ||
        payload.Message ||
        "Request failed";
    captureShownApiError({
        meta,
        statusCode: payload.statusCode ?? payload.StatusCode ?? 400,
        apiData: payload,
        fallbackMessage: msg,
        raw: raw ?? payload,
    });
};

const handleAuthFailure = (apiData) => {
    const isAuth = apiData?.isAuthenticated ?? apiData?.responseItem?.responseContent?.isAuthenticated;
    const message = apiData?.message ?? apiData?.responseItem?.responseContent?.message;
    if (isAuth === false && message) {
        Toast.error(message);
        return false;
    }
    return true;
};

const extractResponseData = (apiData) => {
    const wrapped = apiData?.responseData ?? apiData?.ResponseData;
    if (wrapped?.data != null || wrapped?.Data != null) {
        return {
            data: wrapped.data ?? wrapped.Data,
            message: wrapped.message ?? wrapped.Message,
            statusCode: wrapped.statusCode ?? wrapped.StatusCode,
            success: wrapped.success ?? wrapped.Success,
        };
    }
    if (Array.isArray(wrapped)) {
        return wrapped;
    }
    if (wrapped && typeof wrapped === 'object') {
        // Normalize PascalCase ApiResponse fields from encryption-off wrapper.
        if (
            (wrapped.success != null || wrapped.Success != null) &&
            (wrapped.data != null || wrapped.Data != null)
        ) {
            return {
                data: wrapped.data ?? wrapped.Data,
                message: wrapped.message ?? wrapped.Message,
                statusCode: wrapped.statusCode ?? wrapped.StatusCode,
                success: wrapped.success ?? wrapped.Success,
            };
        }
        return wrapped;
    }
    return apiData;
};

const pickMetaUrl = (meta) =>
    String(meta?.request?.url || meta?.arg?.url || meta?.arg?.endpointName || "");

const pickMetaMethod = (meta) =>
    String(meta?.request?.method || meta?.arg?.method || "").toUpperCase() || null;

const captureShownApiError = ({ meta, statusCode, apiData, fallbackMessage, raw }) => {
    if (isQuietOptionalUrl(meta)) return;

    const body = apiData && typeof apiData === "object" ? apiData : {};
    const methodName =
        body.MethodName ||
        body.methodName ||
        null;
    const errorMessage =
        body.ErrorMessage ||
        body.errorMessage ||
        body.message ||
        body.Message ||
        fallbackMessage ||
        null;
    const innerErrorMessage =
        body.InnerErrorMessage ||
        body.innerErrorMessage ||
        null;

    recordDevApiError({
        url: pickMetaUrl(meta) || methodName,
        method: pickMetaMethod(meta),
        statusCode: statusCode ?? body.statusCode ?? body.StatusCode ?? null,
        methodName,
        errorMessage,
        innerErrorMessage,
        message: body.message || body.Message || fallbackMessage || null,
        raw: raw ?? body,
    });
};

export const transformSuccessResponse = async (response, meta) => {
    let resData = decryptAndParse(response);

    ensureAuthStatusCode(resData, meta);

    // Controllers usually return HTTP 200 with ApiResponse.Success=false for
    // business FailResponse. Capture those into Client API error log so toast-
    // level failures are visible in System Health (not only thrown exceptions).
    if (!isSuccessResponse(resData)) {
        const msg = resData.message || resData.Message || "Something went wrong with API. Please contact admin!!";
        // Do not toast here when the body still carries success/message —
        // page hooks (useAiMutate) already toast. Always capture for the log.
        captureShownApiError({
            meta,
            statusCode: resData?.statusCode ?? resData?.StatusCode ?? 400,
            apiData: resData,
            fallbackMessage: msg,
            raw: resData,
        });
        // Still return payload so callers can read success/message.
        return extractResponseData(resData);
    }

    if (isAuthenticationUrl(meta)) {
        const authCheck = handleAuthFailure(resData);
        if (!authCheck) {
            captureShownApiError({
                meta,
                statusCode: 401,
                apiData: resData,
                fallbackMessage:
                    resData?.message ??
                    resData?.responseItem?.responseContent?.message ??
                    "Authentication failed",
                raw: resData,
            });
            return null;
        }
    }

    const data = extractResponseData(resData);
    captureBusinessFailureIfNeeded(meta, data, resData);
    return data;
};

const normalizeError = (response) => {
    return {
        type: response?.status,
        statusCode:
            response?.data?.responseData?.statusCode ??
            response?.data?.statusCode ??
            response?.originalStatus ??
            (typeof response?.status === "number" ? response.status : null),

        data: response.data,
        raw: response
    };
};

const decryptAndParse = (data) => {
    if (!data) return data;
    if (data.isEnType) {
        const decrypted = decryptAES(data);
        return decrypted ? JSON.parse(decrypted) : data;
    }
    if (data.responseData) {
        return data.responseData;
    }
    return data;
};

const handleValidationError = (resData) => {

    if (!resData?.errors) return;

    const shortenErrorMessage = (message) => {
        if (!message) return message;

        return message.split(" Path:")[0];
    };

    const errorListHtml = Object.entries(resData.errors)
        .flatMap(([field, messages]) => {
            const cleanField = field.replace(/^\$\.?/, ""); // clean up $. or $
            return messages.map(msg => {
                const cleanMsg = shortenErrorMessage(msg);
                return `
                    <div class="swal-validation-error-item">
                        <div class="swal-validation-error-dot"></div>
                        <div class="swal-validation-error-content">
                            ${cleanField ? `<span class="swal-validation-error-field">${cleanField}</span>` : ""}
                            <span class="swal-validation-error-msg">${cleanMsg}</span>
                        </div>
                    </div>
                `;
            });
        })
        .join("");

    Swal.fire({
        title: "Validation Error",
        html: `
            <div class="swal-validation-errors-wrapper">
                <p class="swal-validation-errors-subtitle">Please resolve the following issues to continue:</p>
                <div class="swal-validation-errors-list">
                    ${errorListHtml}
                </div>
            </div>
        `,
        icon: "error",
        customClass: {
            popup: "swal-premium-popup swal-validation-popup",
            title: "swal-premium-title",
            htmlContainer: "swal-premium-html-container",
            confirmButton: "swal-premium-confirm-btn"
        },
        buttonsStyling: false,
        confirmButtonText: "OK"
    });
};


const isQuietOptionalUrl = (meta) => {
    const url = String(meta?.request?.url || meta?.arg?.url || "");
    return url.includes("MolAssistThreads");
};

const handleHttpErrors = (statusCode, resData, meta) => {
    const quiet = isQuietOptionalUrl(meta);
    switch (statusCode) {
        case 400:
            if (resData?.errors) handleValidationError(resData);
            if (!quiet) {
                captureShownApiError({
                    meta,
                    statusCode,
                    apiData: resData,
                    fallbackMessage: "Validation / bad request",
                    raw: resData,
                });
            }
            return resData;

        case 401:
            // Redirect is handled in fetchBaseQuery; avoid duplicate toast spam.
            if (!quiet) {
                const msg = resData?.message || "Session expired. Please login again.";
                Toast.error(msg);
                captureShownApiError({
                    meta,
                    statusCode,
                    apiData: resData,
                    fallbackMessage: msg,
                    raw: resData,
                });
            }
            return null;

        case 500:
            if (!quiet) {
                Toast.error("Something went wrong with the API call! Please contact admin.");
                captureShownApiError({
                    meta,
                    statusCode,
                    apiData: resData,
                    fallbackMessage:
                        resData?.ErrorMessage ||
                        resData?.errorMessage ||
                        "Something went wrong with the API call! Please contact admin.",
                    raw: resData,
                });
            }
            return null;

        default:
            if (!quiet && resData) {
                captureShownApiError({
                    meta,
                    statusCode,
                    apiData: resData,
                    fallbackMessage: resData?.message || resData?.ErrorMessage || "API error",
                    raw: resData,
                });
            }
            return null;
    }
};

export const transformErrorResponse = (response, meta) => {
    const error = normalizeError(response);
    const quiet = isQuietOptionalUrl(meta);

    if (error.type === "FETCH_ERROR") {
        // Authenticated API down / closed: fetchBaseQuery redirects to login.
        // Keep toast quiet when redirect is already in progress from the same failure.
        if (!quiet) {
            const msg = "Unable to reach the server. Please login again.";
            Toast.error(msg);
            captureShownApiError({
                meta,
                statusCode: null,
                apiData: { ErrorMessage: msg },
                fallbackMessage: msg,
                raw: { type: "FETCH_ERROR" },
            });
        }
        return null;
    };

    if (error.type === "PARSING_ERROR" && error.statusCode === 500) {
        if (!quiet) {
            const msg = response.data?.toLowerCase?.() || "";
            const isDbError = ["sql", "connection", "provider"].some(k => msg.includes(k));

            const toastMsg = isDbError
                ? "Database connection issue. Please try again later."
                : "Invalid server response. Please try again later.";
            Toast.error(toastMsg);
            captureShownApiError({
                meta,
                statusCode: 500,
                apiData: {
                    ErrorMessage: toastMsg,
                    MethodName: pickMetaUrl(meta),
                },
                fallbackMessage: toastMsg,
                raw: response.data,
            });
        }
        return null;
    };

    let apiData = decryptAndParse(error.data);

    if (apiData?.responseData) {
        apiData = apiData.responseData;
    }

    ensureAuthStatusCode(apiData, meta);

    if (error.statusCode && error.statusCode !== 200) {
        return handleHttpErrors(error.statusCode, apiData, meta);
    }

    return null;

}
